import { createElement, useCallback, useRef, useState, type ReactNode } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { knowledgeDocumentsApi } from '@/services/api';
import {
  KNOWLEDGE_MAX_DOCUMENT_BYTES,
  KNOWLEDGE_MAX_DOCUMENT_LABEL,
  KNOWLEDGE_UPLOAD_EXTENSIONS,
  KNOWLEDGE_UPLOAD_FORMATS_LABEL,
  KNOWLEDGE_UPLOAD_MIME_TYPES,
} from '@/config';
import { friendlyApiErrorMessage } from '@/utils/composerErrors';
import {
  KnowledgeDocumentConflictDialog,
  type KnowledgeDocumentConflictChoice,
} from '@/components/knowledge/KnowledgeDocumentConflictDialog';
import type { KnowledgeDocument, KnowledgeDocumentStatus, PaginatedResponse } from '@/types';

/**
 * ONE upload sequence for every surface that adds a file to the library
 * (task 1084; source report
 * `project/reports/innovate-knowledge-documents-one-upload-flow.md`).
 *
 * The Documents page, its Add document dialog and the Innovate Assistant
 * sidebar used to own their upload logic separately, which is how they came to
 * name the same file two different ways and how the sidebar dead-ended on a
 * repeat file. This hook owns the whole sequence so they cannot drift again:
 *
 *   1. Envelope check against `src/config` — six extensions, 10 MB — before any
 *      request is spent.
 *   2. The create request, named `file.name` WITH its extension.
 *   3. On 409 `NAME_CONFLICT`, the conflict prompt; the User's choice decides
 *      the resend (`onConflict: replace | keep-both`), nothing (Discard), or —
 *      in the sidebar only — the existing document with no second request.
 *   4. Plan refusals (`no_active_plan`, `limit_exceeded`) go to the caller's
 *      `onPlanError` so each surface shows its needs-a-plan prompt.
 *
 * The caller renders `conflictDialog` wherever it renders its own UI.
 */

/** Plan/entitlement refusal codes from the upload route. */
const PLAN_UPLOAD_ERROR_CODES = new Set(['no_active_plan', 'limit_exceeded']);

const SURFACE_CHOICES: Record<KnowledgeDocumentUploadSurface, KnowledgeDocumentConflictChoice[]> = {
  'documents-page': ['replace', 'keep-both', 'discard'],
  sidebar: ['attach-existing', 'replace', 'keep-both', 'discard'],
};

export type KnowledgeDocumentUploadSurface = 'documents-page' | 'sidebar';

/**
 * What an upload resolves to: the created or replaced document, or — for
 * Attach existing — the existing one as the 409 described it.
 */
export type KnowledgeDocumentUploadResult = Pick<KnowledgeDocument, 'id' | 'name' | 'status'>;

interface UseKnowledgeDocumentUploadOptions {
  surface: KnowledgeDocumentUploadSurface;
  /** Recorded on files this surface creates (the sidebar says where it came from). */
  description?: string;
  /** Called with the refusal code when the upload is refused for the plan. */
  onPlanError?: (code: string) => void;
  /**
   * Called with the message whenever an upload fails (envelope or service),
   * for a caller that uploads several files and needs every reason, not only
   * the last one `error` holds.
   */
  onError?: (message: string) => void;
}

interface PendingConflict {
  fileName: string;
  existingStatus: KnowledgeDocumentStatus;
  resolve: (choice: KnowledgeDocumentConflictChoice) => void;
}

function extensionOf(fileName: string): string {
  const match = /\.([^.]+)$/.exec(fileName);
  return match ? match[1].toLowerCase() : '';
}

/**
 * The local envelope check. Returns the reason a file is refused, or null.
 * The service re-checks and stays the authority.
 */
export function knowledgeUploadRejection(file: File): string | null {
  if (!(KNOWLEDGE_UPLOAD_EXTENSIONS as readonly string[]).includes(extensionOf(file.name))) {
    return `${KNOWLEDGE_UPLOAD_FORMATS_LABEL} files only — “${file.name}” is not one of them.`;
  }
  if (file.size > KNOWLEDGE_MAX_DOCUMENT_BYTES) {
    return `“${file.name}” is larger than the ${KNOWLEDGE_MAX_DOCUMENT_LABEL} limit for a single document.`;
  }
  return null;
}

/**
 * Whether a dragged item MAY be in the envelope. While a drag is in progress
 * the browser exposes each item's MIME type but not its name, and an empty type
 * is common (`.md` on most systems), so only a known, out-of-envelope type is a
 * rejection. The extension check on drop is the real gate.
 */
export function isAcceptableDraggedType(mimeType: string): boolean {
  return mimeType === '' || (KNOWLEDGE_UPLOAD_MIME_TYPES as readonly string[]).includes(mimeType);
}

/** The 409 fields that drive the prompt, or null when the error is not a name conflict. */
function readNameConflict(
  error: unknown
): { existingDocumentId: string; existingStatus: KnowledgeDocumentStatus } | null {
  const { status, response } = (error ?? {}) as { status?: number; response?: unknown };
  if (status !== 409 || !response || typeof response !== 'object') return null;
  const body = response as Record<string, unknown> & { details?: Record<string, unknown> };
  if (body.code !== 'NAME_CONFLICT') return null;
  // Read top-level first (the shape the report specifies), `details` second, so
  // either envelope the service settles on drives the same prompt.
  const existingDocumentId = body.existingDocumentId ?? body.details?.existingDocumentId;
  const existingStatus = body.existingStatus ?? body.details?.existingStatus;
  if (typeof existingDocumentId !== 'string') return null;
  return {
    existingDocumentId,
    existingStatus:
      typeof existingStatus === 'string' ? (existingStatus as KnowledgeDocumentStatus) : 'pending',
  };
}

export function useKnowledgeDocumentUpload({
  surface,
  description,
  onPlanError,
  onError,
}: UseKnowledgeDocumentUploadOptions) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<PendingConflict | null>(null);
  const onPlanErrorRef = useRef(onPlanError);
  onPlanErrorRef.current = onPlanError;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  /**
   * Put a created or replaced document on every cached library page at once,
   * so the row shows up Queued without waiting on a refetch; the list's own
   * status poll carries it to Ready. A replaced document keeps its place.
   */
  const placeInLibrary = useCallback(
    (document: KnowledgeDocument) => {
      queryClient.setQueriesData<PaginatedResponse<KnowledgeDocument>>(
        { queryKey: ['knowledge-documents'] as QueryKey },
        (previous) => {
          if (!previous || !Array.isArray(previous.data)) return previous;
          const present = previous.data.some((row) => row.id === document.id);
          return {
            ...previous,
            data: present
              ? previous.data.map((row) => (row.id === document.id ? document : row))
              : [document, ...previous.data],
          };
        }
      );
      void queryClient.invalidateQueries({ queryKey: ['knowledge-documents'] });
      void queryClient.invalidateQueries({ queryKey: ['knowledge-document', document.id] });
    },
    [queryClient]
  );

  const send = useCallback(
    async (file: File, onConflict?: 'replace' | 'keep-both') => {
      const response = await knowledgeDocumentsApi.upload(
        file,
        { name: file.name, description },
        onConflict
      );
      const document = response.data.data;
      placeInLibrary(document);
      return document;
    },
    [description, placeInLibrary]
  );

  const upload = useCallback(
    async (file: File): Promise<KnowledgeDocumentUploadResult | null> => {
      setError(null);
      const rejection = knowledgeUploadRejection(file);
      if (rejection) {
        setError(rejection);
        onErrorRef.current?.(rejection);
        return null;
      }

      setUploading(true);
      try {
        try {
          return await send(file);
        } catch (firstError) {
          const nameConflict = readNameConflict(firstError);
          if (!nameConflict) throw firstError;

          const choice = await new Promise<KnowledgeDocumentConflictChoice>((resolve) => {
            setConflict({
              fileName: file.name,
              existingStatus: nameConflict.existingStatus,
              resolve,
            });
          });
          setConflict(null);

          if (choice === 'discard') return null;
          if (choice === 'attach-existing') {
            return {
              id: nameConflict.existingDocumentId,
              name: file.name,
              status: nameConflict.existingStatus,
            };
          }
          return await send(file, choice);
        }
      } catch (uploadError) {
        const code = uploadError instanceof Error ? uploadError.message : '';
        if (PLAN_UPLOAD_ERROR_CODES.has(code)) {
          onPlanErrorRef.current?.(code);
        }
        const message =
          friendlyApiErrorMessage(uploadError) ??
          (surface === 'sidebar'
            ? `That file could not be attached: ${code || 'unknown error'}`
            : code || 'Could not add the document. Please try again.');
        setError(message);
        onErrorRef.current?.(message);
        return null;
      } finally {
        setUploading(false);
      }
    },
    [send, surface]
  );

  const clearError = useCallback(() => setError(null), []);

  const conflictDialog: ReactNode = conflict
    ? createElement(KnowledgeDocumentConflictDialog, {
        isOpen: true,
        fileName: conflict.fileName,
        existingStatus: conflict.existingStatus,
        choices: SURFACE_CHOICES[surface],
        onChoose: conflict.resolve,
      })
    : null;

  return { upload, uploading, error, clearError, conflictDialog };
}

export default useKnowledgeDocumentUpload;
